import { useMemo } from "react";
import { monate as frequenzMonate } from "../lib/frequenz";
import type { KatalogEintrag, VertragPosition } from "../lib/wizard";
import { positionTitel } from "../lib/wizard";
import { eur } from "../hooks/useVertraege";
import { zahl } from "../lib/zahl";

const MONATE = ["Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember"];

type MonatsZeile = {
  monat: number;
  leistungen: string[];
  wert: number;
  stunden: number;
};

/** Was in welchem Monat ansteht — aus den Positionen gerechnet, nichts
 *  gespeichert. Ein Monat ohne Leistung bleibt bewusst in der Liste: die Lücke
 *  ist die eigentliche Information (wo staut sich Arbeit, wo passiert nichts). */
export function jahresplan(
  positionen: VertragPosition[], katalog: KatalogEintrag[], faktor: number,
): MonatsZeile[] {
  const zeilen: MonatsZeile[] = MONATE.map((_, i) => ({
    monat: i + 1, leistungen: [], wert: 0, stunden: 0,
  }));
  for (const p of positionen) {
    if (p.optional && !p.beauftragt) continue;
    // MIT Mehrfachnennung: zwei Punkte im März sind zwei Einsätze und zählen
    // zweimal — in Wert, Stunden und Liste. Genau das soll der Plan zeigen.
    const monate = frequenzMonate(p);
    const jeDurchgang = Math.round(p.einzelpreis_netto * faktor * 100) / 100 * p.menge;
    const titel = positionTitel(p, katalog) || "Leistung";
    for (const m of monate) {
      const z = zeilen[m - 1];
      if (!z) continue;
      z.leistungen.push(titel);
      z.wert += jeDurchgang;
      z.stunden += (p.zeit_min_snapshot * p.menge) / 60;
    }
  }
  return zeilen;
}

/**
 * Jahresplan als eigene Spalte (ab 1024 px) bzw. als Blatt von rechts.
 *
 * Beim Zusammenstellen der Leistungen ist die wichtigste Frage nicht „was
 * kostet es", sondern „wann passiert es" — vier Einsätze im Juni und keiner im
 * August ist ein Planungsfehler, den man nur sieht, wenn man das Jahr am Stück
 * vor sich hat.
 */
export default function Jahresplan({
  positionen, katalog, faktor, offen, onSchliessen,
}: {
  positionen: VertragPosition[];
  katalog: KatalogEintrag[];
  faktor: number;
  offen: boolean;
  onSchliessen: () => void;
}) {
  const zeilen = useMemo(() => jahresplan(positionen, katalog, faktor), [positionen, katalog, faktor]);
  const jahreswert = zeilen.reduce((s, z) => s + z.wert, 0);
  const stunden = zeilen.reduce((s, z) => s + z.stunden, 0);
  const einsatzmonate = zeilen.filter(z => z.leistungen.length > 0).length;
  const spitze = Math.max(1, ...zeilen.map(z => z.leistungen.length));

  return (
    <>
      {offen && <div className="pf-plan-hinterhang" onClick={onSchliessen} />}
      <aside className={"pf-plan" + (offen ? " is-offen" : "")} aria-label="Jahresplan">
        <div className="pf-plan-kopf">
          <div className="pf-plan-titel">Jahresplan</div>
          <button className="pf-linkbtn pf-plan-schliessen" onClick={onSchliessen}>Schließen</button>
        </div>
        <div className="pf-plan-koerper">
          {zeilen.map(z => (
            <div key={z.monat} className={"pf-plan-monat" + (z.leistungen.length ? "" : " is-leer")}>
              <div className="pf-plan-monat-kopf">
                <span className="pf-plan-name">{MONATE[z.monat - 1]}</span>
                {z.leistungen.length > 0 && <span className="pf-plan-wert">{eur(z.wert)}</span>}
              </div>
              {z.leistungen.length === 0 ? (
                <div className="pf-plan-meta">kein Einsatz</div>
              ) : (
                <>
                  <div className="pf-plan-zeile">{z.leistungen.join(" · ")}</div>
                  <div className="pf-plan-meta">
                    {z.leistungen.length} {z.leistungen.length === 1 ? "Leistung" : "Leistungen"}
                    {z.stunden > 0 ? ` · rund ${zahl(z.stunden, 1)} h` : ""}
                    {z.leistungen.length === spitze && spitze > 1 ? " · vollster Monat" : ""}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="pf-plan-fuss">
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
            <span style={{ color: "var(--pf-ink-3)" }}>Jahreswert netto</span>
            <strong>{eur(jahreswert)}</strong>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 3 }}>
            <span style={{ color: "var(--pf-ink-3)" }}>Einsatzmonate</span>
            <span>{einsatzmonate} von 12{stunden > 0 ? ` · rund ${zahl(stunden, 1)} h` : ""}</span>
          </div>
        </div>
      </aside>
    </>
  );
}
